import { Link, useNavigate } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { ArrowLeft, Check, Download, Pencil, Send, Truck, XCircle } from "lucide-react";
import { toast } from "sonner";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { apiDownload, isApiConfigured } from "@/lib/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDispatchNote, useUpdateDispatchStatus } from "@/features/dispatch/api";
import {
  DISPATCH_STATUSES,
  canEditDispatch,
  labelFor,
  statusTone,
} from "@/features/dispatch/constants";
import type { DispatchStatus } from "@/features/dispatch/types";
import { fmtQty } from "@/features/inventory/constants";

export function DispatchDetailPage({ dnId }: { dnId: string }) {
  return (
    <ErrorBoundary title="Dispatch detail crashed">
      <DispatchDetailInner dnId={dnId} />
    </ErrorBoundary>
  );
}

function DispatchDetailInner({ dnId }: { dnId: string }) {
  const navigate = useNavigate();
  const { data: note, isLoading, error } = useDispatchNote(dnId);
  const updateStatus = useUpdateDispatchStatus();

  if (isLoading) {
    return <p className="py-16 text-center text-sm text-muted-foreground">Loading…</p>;
  }
  if (error || !note) {
    return (
      <EmptyState
        title="Dispatch not found"
        description={(error as Error | undefined)?.message ?? "Unknown note"}
        action={
          <Button asChild size="sm" variant="outline">
            <Link to="/dispatch">Back</Link>
          </Button>
        }
      />
    );
  }

  function setStatus(status: DispatchStatus) {
    updateStatus.mutate({ id: dnId, status });
  }

  async function downloadPdf() {
    try {
      await apiDownload(`/pdf/dispatch-notes/${dnId}`, `${note?.note_number || "dn"}.pdf`);
    } catch (e) {
      toast.error((e as Error).message || "PDF download failed");
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-3 pb-8">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <Button type="button" variant="ghost" size="sm" className="mb-1 -ml-2" asChild>
            <Link to="/dispatch">
              <ArrowLeft />
              Dispatch
            </Link>
          </Button>
          <h1 className="font-mono text-lg font-semibold tracking-tight">{note.note_number}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <StatusDot tone={statusTone(note.status)}>
              {labelFor(DISPATCH_STATUSES, note.status)}
            </StatusDot>
            <span>{format(parseISO(note.dispatch_date), "dd MMM yyyy")}</span>
            <span>
              {note.client_code} · {note.client_name}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {isApiConfigured() ? (
            <Button type="button" variant="outline" size="sm" onClick={() => void downloadPdf()}>
              <Download />
              Download PDF
            </Button>
          ) : null}
          {canEditDispatch(note.status) ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate({ to: "/dispatch/$dnId/edit", params: { dnId } })}
            >
              <Pencil />
              Edit
            </Button>
          ) : null}
          {note.status === "draft" ? (
            <Button
              type="button"
              size="sm"
              disabled={updateStatus.isPending}
              onClick={() => setStatus("loading")}
            >
              <Send />
              Start loading
            </Button>
          ) : null}
          {note.status === "draft" || note.status === "loading" ? (
            <Button
              type="button"
              size="sm"
              disabled={updateStatus.isPending}
              onClick={() => setStatus("dispatched")}
            >
              <Truck />
              Mark dispatched
            </Button>
          ) : null}
          {note.status === "draft" || note.status === "loading" ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={updateStatus.isPending}
              onClick={() => setStatus("cancelled")}
            >
              <XCircle />
              Cancel
            </Button>
          ) : null}
          {note.status === "dispatched" ? (
            <Button type="button" size="sm" variant="outline" disabled>
              <Check />
              Dispatched
            </Button>
          ) : null}
        </div>
      </div>

      <div className="grid gap-3 text-xs md:grid-cols-2">
        <section className="rounded border border-border bg-card p-3 space-y-1.5">
          <h2 className="font-medium">Links</h2>
          <div>
            SO{" "}
            <Link
              to="/sales-orders/$soId"
              params={{ soId: note.sales_order_id }}
              className="font-mono text-primary hover:underline"
            >
              {note.so_number}
            </Link>
          </div>
          {note.invoice_id && note.invoice_number ? (
            <div>
              Invoice{" "}
              <Link
                to="/invoices/$invId"
                params={{ invId: note.invoice_id }}
                className="font-mono text-primary hover:underline"
              >
                {note.invoice_number}
              </Link>
            </div>
          ) : (
            <div className="text-muted-foreground">No invoice linked</div>
          )}
          <div className="pt-1 text-muted-foreground">
            Total wt {fmtQty(note.total_weight)} · qty {fmtQty(note.total_quantity)}
          </div>
        </section>
        <section className="rounded border border-border bg-card p-3 space-y-1.5">
          <h2 className="font-medium">Logistics</h2>
          <div>Transporter: {note.transporter_name || "—"}</div>
          <div>Vehicle: {note.vehicle_number || "—"}</div>
          <div>Driver: {note.driver_name || "—"}</div>
          <div>LR: {note.lr_number || "—"}</div>
          {note.instructions ? (
            <div className="pt-1 text-muted-foreground">{note.instructions}</div>
          ) : null}
        </section>
      </div>

      {note.ship_to_text ? (
        <section className="rounded border border-border bg-card p-3 text-xs">
          <h2 className="mb-1 font-medium">Ship to</h2>
          <pre className="whitespace-pre-wrap font-sans text-muted-foreground">{note.ship_to_text}</pre>
        </section>
      ) : null}

      <section className="rounded border border-border bg-card">
        <div className="border-b border-border px-3 py-2 text-xs font-semibold">Lines</div>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>#</TableHead>
              <TableHead>SO line</TableHead>
              <TableHead>Batch</TableHead>
              <TableHead>Material</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead className="text-right">Weight</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(note.lines ?? []).map((line) => (
              <TableRow key={line.id}>
                <TableCell className="tabular-nums">{line.line_no}</TableCell>
                <TableCell className="tabular-nums">#{line.so_line_no ?? "—"}</TableCell>
                <TableCell>
                  <Link
                    to="/inventory/$batchId"
                    params={{ batchId: line.inventory_batch_id }}
                    className="font-mono text-xs text-primary hover:underline"
                  >
                    {line.batch_no}
                  </Link>
                </TableCell>
                <TableCell className="text-xs">{line.material_name}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtQty(line.quantity)}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtQty(line.weight)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </section>

      {note.notes ? (
        <p className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">{note.notes}</p>
      ) : null}
    </div>
  );
}
